import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import PageHeader from '@/components/feature/PageHeader';
import TopicCard from '@/components/feature/TopicCard';
import { categories } from '@/mocks/categories';
import { figures } from '@/mocks/figures';
import { topics } from '@/mocks/topics';

const categoryNames = categories.map((c) => c.name);

const topicsJsonLd = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'CollectionPage',
      name: '秘史专题｜独家黑料档案中心',
      inLanguage: 'zh-CN',
      description: '以事件复盘、秘史脉络、时间线整理与资料考据为核心的深度专题列表。',
    },
    {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: 'https://hnitsc.cn/' },
        { '@type': 'ListItem', position: 2, name: '秘史专题', item: 'https://hnitsc.cn/topics' },
      ],
    },
    {
      '@type': 'ItemList',
      name: '秘史专题列表',
      itemListElement: topics.map((t, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        name: t.title,
        description: t.excerpt,
        url: `https://hnitsc.cn/topics/${t.slug}`,
      })),
    },
  ],
};

export default function TopicsPage() {
  const [params, setParams] = useSearchParams();
  const initialCategory = params.get('category') || '全部';
  const [keyword, setKeyword] = useState(params.get('q') || '');
  const [category, setCategory] = useState(categoryNames.includes(initialCategory) ? initialCategory : '全部');
  const [layout, setLayout] = useState<'grid' | 'list'>('grid');

  const figureId = params.get('figure');
  const figureName = figureId ? figures.find((f) => f.id === figureId)?.name : undefined;

  const filtered = useMemo(() => {
    const kw = keyword.trim();
    return topics.filter((t) => {
      const matchCat = category === '全部' || t.category === category;
      const matchKw =
        !kw ||
        t.title.includes(kw) ||
        t.excerpt.includes(kw) ||
        t.tags.some((tag) => tag.includes(kw));
      const matchFigure = !figureId || t.relatedFigures.includes(figureId);
      return matchCat && matchKw && matchFigure;
    });
  }, [keyword, category, figureId]);

  const updateCategory = (name: string) => {
    setCategory(name);
    const next = new URLSearchParams(params);
    if (name === '全部') next.delete('category');
    else next.set('category', name);
    setParams(next, { replace: true });
  };

  const clearFigureFilter = () => {
    const next = new URLSearchParams(params);
    next.delete('figure');
    setParams(next, { replace: true });
  };

  return (
    <>
      <PageHeader
        crumbs={[
          { label: '首页', to: '/' },
          { label: '秘史专题' },
        ]}
        eyebrow="秘史专题"
        title="把论证过程一起写出来的深度长文"
        description="这里不只给结论。每篇专题都会交代用了哪些材料、哪些地方存在分歧、哪些结论仍然存疑。你可以顺着正文的引用顺序，自己核对一遍。"
        image="https://readdy.ai/api/search-image?query=Research%20desk%20with%20open%20notebooks%2C%20loose%20photographs%20and%20a%20vintage%20typewriter%2C%20warm%20amber%20light%2C%20aged%20paper%20tones%2C%20editorial%20archive%20mood%2C%20no%20people&width=1400&height=600&seq=topics-banner-05&orientation=landscape"
        imageAlt="笔记本、老照片与打字机组成的研究桌面，象征秘史专题的考据过程"
        imageTitle="秘史专题 · 独家黑料档案中心"
      />

      <section className="w-full bg-background-50">
        <div className="mx-auto max-w-content px-4 py-10 md:px-8 md:py-14">
          <div className="flex flex-col gap-4 rounded-lg border border-background-200 bg-background-100 p-4 md:flex-row md:items-center md:justify-between md:p-5">
            <div className="flex w-full items-center gap-2 rounded-md border border-background-300 bg-background-50 px-4 py-3 md:max-w-sm">
              <i className="ri-search-line text-base text-foreground-400" aria-hidden="true"></i>
              <input
                type="search"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索专题标题或标签"
                aria-label="搜索秘史专题"
                className="w-full bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none"
              />
            </div>

            <div className="flex items-center gap-1 self-start rounded-full border border-background-300 bg-background-50 p-1">
              <button
                type="button"
                onClick={() => setLayout('grid')}
                aria-label="切换为卡片视图"
                className={`flex h-8 w-8 items-center justify-center rounded-full transition-colors ${
                  layout === 'grid' ? 'bg-primary-700 text-background-50' : 'text-foreground-600'
                }`}
              >
                <i className="ri-grid-fill" aria-hidden="true"></i>
              </button>
              <button
                type="button"
                onClick={() => setLayout('list')}
                aria-label="切换为列表视图"
                className={`flex h-8 w-8 items-center justify-center rounded-full transition-colors ${
                  layout === 'list' ? 'bg-primary-700 text-background-50' : 'text-foreground-600'
                }`}
              >
                <i className="ri-list-check-2" aria-hidden="true"></i>
              </button>
            </div>
          </div>

          <div className="mt-5 flex flex-wrap items-center gap-2">
            {['全部', ...categoryNames].map((name) => (
              <button
                key={name}
                type="button"
                onClick={() => updateCategory(name)}
                className={`whitespace-nowrap rounded-full border px-4 py-1.5 text-xs font-medium transition-colors ${
                  category === name
                    ? 'border-primary-700 bg-primary-700 text-background-50'
                    : 'border-background-300 bg-background-50 text-foreground-700 hover:border-primary-400 hover:text-primary-800'
                }`}
              >
                {name}
              </button>
            ))}
          </div>

          {figureName && (
            <div className="mt-5 flex items-center gap-3 rounded-md border border-secondary-300 bg-secondary-100 px-4 py-3 text-sm text-secondary-900">
              <i className="ri-user-search-line" aria-hidden="true"></i>
              <span>正在查看与「{figureName}」相关的专题</span>
              <button
                type="button"
                onClick={clearFigureFilter}
                className="ml-auto whitespace-nowrap rounded-md border border-secondary-400 px-3 py-1 text-xs transition-colors hover:bg-secondary-200"
              >
                清除人物筛选
              </button>
            </div>
          )}

          <p className="mt-5 text-sm text-foreground-500">
            共 <strong className="text-foreground-900">{filtered.length}</strong> 篇专题
            {category !== '全部' && ` · 分类：${category}`}
          </p>

          {filtered.length > 0 ? (
            <div
              className={
                layout === 'grid'
                  ? 'mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3'
                  : 'mt-6 flex flex-col gap-4'
              }
            >
              {filtered.map((topic) => (
                <TopicCard key={topic.id} topic={topic} variant={layout} />
              ))}
            </div>
          ) : (
            <div className="mt-10 flex flex-col items-center rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-16 text-center">
              <span className="flex h-12 w-12 items-center justify-center rounded-md bg-background-200 text-foreground-500">
                <i className="ri-file-search-line text-2xl" aria-hidden="true"></i>
              </span>
              <p className="mt-4 font-heading text-base font-semibold text-foreground-950">
                没有找到匹配的专题
              </p>
              <p className="mt-2 max-w-md text-sm text-foreground-600">
                换一个关键词，或点击“全部”查看所有栏目。也可以先去人物库找找相关人物。
              </p>
              <button
                type="button"
                onClick={() => {
                  setKeyword('');
                  updateCategory('全部');
                  clearFigureFilter();
                }}
                className="mt-5 whitespace-nowrap rounded-md bg-primary-700 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-800"
              >
                清空所有筛选
              </button>
            </div>
          )}
        </div>
      </section>

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(topicsJsonLd) }} />
    </>
  );
}